import React, { useLayoutEffect, useMemo, useState } from 'react';

/**
 * ScaleStage
 * - baseWidth/baseHeight(예: 375x812) 기준으로 화면에 비율 유지 스케일
 * - 기본 padding을 0으로 두어 불필요한 검정 여백이 생기지 않게 함
 * - topInset(예: ElectronTitleBar 높이) 만큼 실제 사용 가능 높이에서 제외
 * - transform scale 사용 시 레이아웃 박스가 커지지 않는 문제를 방지하기 위해
 *   stageWrap(스케일된 W/H)로 레이아웃을 먼저 확보한 뒤 내부에서 scale 적용
 */
export default function ScaleStage({
  baseWidth = 375,
  baseHeight = 812,
  maxScale = 999,
  minScale = 0.2,
  outerBackground = '#000',
  children,
  padding = 0,
  topInset = 0,
}) {
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const readViewport = () => {
      const vv = window.visualViewport;
      const vw = Math.round(vv?.width || window.innerWidth || baseWidth);
      const vh = Math.round(vv?.height || window.innerHeight || baseHeight);
      return { vw, vh };
    };

    const calc = () => {
      const { vw, vh } = readViewport();

      const safeTopInset = Math.max(0, Number(topInset) || 0);
      const availW = Math.max(0, vw - padding * 2);
      const availH = Math.max(0, vh - safeTopInset - padding * 2);

      const next = Math.min(availW / baseWidth, availH / baseHeight);
      const clamped = Math.max(minScale, Math.min(maxScale, next));
      setScale(Number.isFinite(clamped) && clamped > 0 ? clamped : 1);
    };

    calc();

    let rafId = 0;
    const onResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(calc);
    };

    const t0 = setTimeout(calc, 0);
    const t1 = setTimeout(calc, 50);
    const t2 = setTimeout(calc, 200);

    window.addEventListener('resize', onResize);

    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener('resize', onResize);
      vv.addEventListener('scroll', onResize);
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener('resize', onResize);
      if (vv) {
        vv.removeEventListener('resize', onResize);
        vv.removeEventListener('scroll', onResize);
      }
    };
  }, [baseWidth, baseHeight, maxScale, minScale, padding, topInset]);

  const wrapStyle = useMemo(() => {
    const w = Math.round(baseWidth * scale);
    const h = Math.round(baseHeight * scale);
    return {
      width: w,
      height: h,
      position: 'relative',
      flex: '0 0 auto',
    };
  }, [baseWidth, baseHeight, scale]);

  const stageStyle = useMemo(
    () => ({
      width: baseWidth,
      height: baseHeight,
      position: 'absolute',
      left: 0,
      top: 0,
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
    }),
    [baseWidth, baseHeight, scale]
  );

  const rootStyle = useMemo(
    () => ({
      width: '100vw',
      height: `calc(100vh - ${Math.max(0, Number(topInset) || 0)}px)`,
      marginTop: Math.max(0, Number(topInset) || 0),
      background: outerBackground,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      padding,
      boxSizing: 'border-box',
    }),
    [outerBackground, padding, topInset]
  );

  return (
    <div style={rootStyle}>
      <div style={wrapStyle}>
        <div style={stageStyle}>{children}</div>
      </div>
    </div>
  );
}
